//
// # Context Helpers for IDE
//
// Creates Context objects for request tracing across the system.
// This wraps CoreUtils context functions with IDE-specific defaults.
//

import {
  Context,
  createRequestContext as baseCreateRequestContext,
  createBackgroundContext as baseCreateBackgroundContext,
  type ContextOptions,
} from "CoreUtils";

// Definitions made at compile time (same as in main.tsx)
interface CompilerDefs {
  GIT_INFO: { commitHash: string };
  ALPHA_SERVER_URL: string;
  OS_USERNAME: string;
}
declare const COMPILER_DEFS: CompilerDefs;

// Side-effect import: pulls in the Window.__GENALPHA_CONFIG__ global type declaration.
import "./config";

// Per-tab session ID. The IDE has exactly one session per browser tab, so
// a module-level singleton is the natural fit. Set once at AlphaClient
// construction; thereafter every Context produced via the helpers below
// inherits it, enabling session-level correlation in the log dashboard.
let _sessionId: string | undefined;

// Authenticated user's email. Same one-per-tab assumption as sessionId.
let _userEmail: string | undefined;

/**
 * Set the session ID for this browser tab. Called once when the
 * AlphaClient is constructed.
 */
export function setSessionId(sessionId: string): void {
  _sessionId = sessionId;
}

/**
 * Set the authenticated user's email for this tab. Called from the
 * AlphaClient constructor; falls back to OS username when not set.
 */
export function setUserEmail(email: string | undefined): void {
  _userEmail = email;
}

/**
 * Get the context options for IDE environment.
 *
 * The `user` field resolves in this order:
 *   1. caller-supplied override (legacy `userId` arg, retained for back-compat)
 *   2. authenticated user email (module state from AlphaClient)
 *   3. OS username baked in at build time (local dev when auth is off)
 *   4. undefined (no user field set on the context)
 *
 * We deliberately do NOT fall back to clientId; sessionId already
 * provides per-tab correlation in the log dashboard.
 */
function getContextOptions(userIdOverride?: string): ContextOptions {
  const osUsername =
    typeof COMPILER_DEFS !== "undefined" ? COMPILER_DEFS.OS_USERNAME : "";
  return {
    env: "ide",
    tenant: window.__GENALPHA_CONFIG__?.tenancy ?? "default",
    user: userIdOverride ?? _userEmail ?? (osUsername || undefined),
    sessionId: _sessionId,
    commitHash:
      typeof COMPILER_DEFS !== "undefined"
        ? COMPILER_DEFS.GIT_INFO.commitHash
        : undefined,
  };
}

/**
 * Create a new Context for a user-initiated request.
 * Populates standard context values like user identity, environment, and log ID.
 *
 * @param userId - Optional user identifier (e.g., the client ID)
 */
export function createRequestContext(userId?: string): Context {
  return baseCreateRequestContext(getContextOptions(userId));
}

/**
 * Snapshot of the IDE's IDE-wide identity for the Report-an-Issue payload
 * (issue #2146). Reads the same module/window/COMPILER_DEFS state as
 * getContextOptions so the report context matches the Context that
 * other API calls travel under.
 */
export interface ReportIssueDefaults {
  sessionId: string;
  userEmail: string;
  tenant: string;
  env: string;
  commitHash?: string;
}

export function getReportIssueDefaults(): ReportIssueDefaults {
  const osUsername =
    typeof COMPILER_DEFS !== "undefined" ? COMPILER_DEFS.OS_USERNAME : "";
  const commitHash =
    typeof COMPILER_DEFS !== "undefined"
      ? COMPILER_DEFS.GIT_INFO.commitHash
      : undefined;
  return {
    sessionId: _sessionId ?? "unset",
    userEmail: _userEmail ?? osUsername,
    tenant: window.__GENALPHA_CONFIG__?.tenancy ?? "default",
    env: "ide",
    commitHash,
  };
}

/**
 * Create a Context for internal/background operations.
 * Use this instead of Context.background() to get full context with
 * env, tenant, and commitHash populated.
 */
export function createBackgroundContext(): Context {
  return baseCreateBackgroundContext(getContextOptions());
}
